import React, { useEffect, useRef, useState } from "react";
import {
  fetchTopicCategories,
  adminListCategoryGroups,
  adminCreateCategoryGroup,
  adminUpdateCategoryGroup,
  adminDeleteCategoryGroup,
  adminAddCategoryGroupMember,
  adminRemoveCategoryGroupMember,
  practiceAdminSearchUsers,
  adminListInstitutes,
} from "../api";
import { CategoryIcon } from "../categoryIcons";

/**
 * Admin panel → "Category groups": access groups for *restricted* topic
 * categories. A group holds a set of categories and a set of members; members
 * (and admins) are the only people who can see a restricted category and its
 * topics. Unrestricted categories stay visible to everyone, so attaching one
 * is harmless (the list says so). Membership is admin-managed by email — no
 * self-serve join — and never grants app access. A whole institute can be a
 * member too: everyone holding a valid seat in it (operational institute,
 * student/teacher or seated admin) is treated as a member.
 */
export default function AdminCategoryGroupsPanel() {
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      adminListCategoryGroups(),
      fetchTopicCategories(),
      adminListInstitutes().catch(() => ({ institutes: [] })),
    ])
      .then(([g, cats, inst]) => {
        setGroups(g.groups || []);
        setCategories(cats || []);
        setInstitutes(inst.institutes || []);
      })
      .catch((err) => setError(err.message || "Failed to load category groups"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const replaceGroup = (updated) =>
    setGroups((list) => list.map((g) => (g.id === updated.id ? updated : g)));

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const created = await adminCreateCategoryGroup({ name, description: form.description.trim() });
      setGroups((list) => [...list, created].sort((a, b) => a.name.localeCompare(b.name)));
      setForm({ name: "", description: "" });
    } catch (err) {
      setError(err.message || "Could not create the group.");
    } finally {
      setCreating(false);
    }
  };

  const restrictedCount = categories.filter((c) => c.is_restricted).length;

  return (
    <div className="space-y-6" data-testid="category-groups-panel">
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-1">Create a group</h3>
        <p className="text-xs text-slate-500 mb-3">
          Members of a group can see the <span className="font-medium">restricted</span> categories
          attached to it (and their topics). Mark a category as restricted in the sidebar's
          Categories manager; unrestricted categories are visible to everyone anyway. Add people
          by email, or add a whole institute — everyone holding a valid seat in it counts as a
          member. Membership only controls visibility — it doesn't change anyone's subscription.
        </p>
        <form onSubmit={handleCreate} className="space-y-3 surface-glass rounded-xl p-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Group name</label>
            <input
              type="text"
              value={form.name}
              maxLength={120}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Cohort A"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              aria-label="Group name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description (optional)</label>
            <input
              type="text"
              value={form.description}
              maxLength={255}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Who this group is for"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              aria-label="Group description"
            />
          </div>
          <button
            type="submit"
            disabled={creating || !form.name.trim()}
            className="px-4 py-2 rounded-lg bg-[var(--gold)] text-[var(--ink)] text-sm font-semibold hover:brightness-110 disabled:opacity-50"
          >
            {creating ? "Creating…" : "Create group"}
          </button>
        </form>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-1">Groups</h3>
        {!loading && restrictedCount === 0 && categories.length > 0 && (
          <p className="text-xs text-amber-700 mb-3">
            No category is restricted yet, so groups don't hide anything. Tick "Restricted" on a
            category in the sidebar's Categories manager to gate it.
          </p>
        )}
        {loading && <p className="text-sm text-slate-500">Loading groups…</p>}
        {!loading && groups.length === 0 && (
          <p className="text-sm text-slate-500">No groups yet.</p>
        )}
        <div className="space-y-3">
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              categories={categories}
              institutes={institutes}
              onChanged={replaceGroup}
              onDeleted={(id) => setGroups((list) => list.filter((x) => x.id !== id))}
              onError={setError}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function GroupCard({ group, categories, institutes, onChanged, onDeleted, onError }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(group.name);
  const [editDescription, setEditDescription] = useState(group.description || "");
  const [memberEmail, setMemberEmail] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [memberMsg, setMemberMsg] = useState(null);
  const searchTimer = useRef(null);

  const run = async (fn) => {
    setBusy(true);
    onError(null);
    try {
      await fn();
    } catch (err) {
      onError(err.message || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const selected = new Set(group.category_ids || []);
  const selectedInstitutes = new Set(group.institute_ids || []);

  const toggleCategory = (catId) =>
    run(async () => {
      const next = new Set(selected);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      onChanged(await adminUpdateCategoryGroup(group.id, { category_ids: Array.from(next) }));
    });

  const toggleInstitute = (instId) =>
    run(async () => {
      const next = new Set(selectedInstitutes);
      if (next.has(instId)) next.delete(instId);
      else next.add(instId);
      onChanged(await adminUpdateCategoryGroup(group.id, { institute_ids: Array.from(next) }));
    });

  const saveEdit = (e) => {
    e.preventDefault();
    const name = editName.trim();
    if (!name) return;
    run(async () => {
      onChanged(await adminUpdateCategoryGroup(group.id, { name, description: editDescription.trim() }));
      setEditing(false);
    });
  };

  const handleDelete = () => {
    const n = group.member_count || 0;
    const msg =
      `Delete "${group.name}"?` +
      (n > 0 ? `\n\n${n} ${n === 1 ? "member loses" : "members lose"} access to its restricted categories.` : "") +
      "\n\nCategories and topics are not deleted.";
    if (!window.confirm(msg)) return;
    run(async () => {
      await adminDeleteCategoryGroup(group.id);
      onDeleted(group.id);
    });
  };

  const searchUsers = (q) => {
    clearTimeout(searchTimer.current);
    if (!q.trim() || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const users = await practiceAdminSearchUsers(q.trim());
        const already = new Set((group.members || []).map((m) => m.email.toLowerCase()));
        setSuggestions((users || []).filter((u) => u.email && !already.has(u.email.toLowerCase())));
      } catch {
        setSuggestions([]);
      }
    }, 250);
  };

  const addMember = (email) => {
    const value = (email || memberEmail).trim();
    if (!value) return;
    setMemberMsg(null);
    setShowSuggestions(false);
    run(async () => {
      const updated = await adminAddCategoryGroupMember(group.id, value);
      onChanged(updated);
      setMemberEmail("");
      setSuggestions([]);
      setMemberMsg(`${value} added.`);
    });
  };

  const removeMember = (m) => {
    if (!window.confirm(`Remove ${m.email} from "${group.name}"? They lose access to its restricted categories.`)) return;
    run(async () => onChanged(await adminRemoveCategoryGroupMember(group.id, m.id)));
  };

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`category-group-${group.id}`}>
      {/* Header */}
      {editing ? (
        <form onSubmit={saveEdit} className="space-y-2">
          <input
            type="text"
            value={editName}
            maxLength={120}
            onChange={(e) => setEditName(e.target.value)}
            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            aria-label="Group name"
            autoFocus
          />
          <input
            type="text"
            value={editDescription}
            maxLength={255}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Description (optional)"
            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            aria-label="Group description"
          />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !editName.trim()} className="px-3 py-1.5 rounded-lg bg-[var(--gold)] text-[var(--ink)] text-xs font-semibold disabled:opacity-50">
              Save
            </button>
            <button type="button" onClick={() => { setEditing(false); setEditName(group.name); setEditDescription(group.description || ""); }} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-800 truncate">{group.name}</div>
            {group.description && <p className="text-xs text-slate-500 mt-0.5">{group.description}</p>}
            <p className="text-xs text-slate-400 mt-1">
              {group.member_count} {group.member_count === 1 ? "member" : "members"}
              {selectedInstitutes.size > 0 && (
                <> · {selectedInstitutes.size} {selectedInstitutes.size === 1 ? "institute" : "institutes"}</>
              )}
              {" "}· {selected.size} {selected.size === 1 ? "category" : "categories"}
            </p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button type="button" onClick={() => setEditing(true)} disabled={busy} className="px-2.5 py-1 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              Edit
            </button>
            <button type="button" onClick={handleDelete} disabled={busy} className="px-2.5 py-1 rounded-lg text-rose-500 text-xs font-medium hover:bg-rose-50 disabled:opacity-50">
              Delete…
            </button>
          </div>
        </div>
      )}

      {/* Categories */}
      <div>
        <div className="text-xs font-medium text-slate-600 mb-1.5">Categories</div>
        {categories.length === 0 ? (
          <p className="text-xs text-slate-400">No categories exist yet — add some in the sidebar's Categories manager.</p>
        ) : (
          <ul className="space-y-1">
            {categories.map((cat) => (
              <li key={cat.id}>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selected.has(cat.id)}
                    disabled={busy}
                    onChange={() => toggleCategory(cat.id)}
                    className="w-4 h-4 rounded"
                    style={{ accentColor: "var(--gold)" }}
                    aria-label={`${cat.name} in ${group.name}`}
                  />
                  <CategoryIcon icon={cat.icon} className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{cat.name}</span>
                  {cat.is_restricted ? (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">Restricted</span>
                  ) : (
                    <span className="text-[10px] text-slate-400 shrink-0" title="Not restricted: everyone can see this category regardless of groups">
                      visible to all
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Institutes */}
      {institutes.length > 0 && (
        <div>
          <div className="text-xs font-medium text-slate-600 mb-0.5">Institutes</div>
          <p className="text-[11px] text-slate-400 mb-1.5">
            Everyone with a valid seat in a ticked institute is a member (students, teachers, and
            the admin if they use a seat). Pending, disabled or unpaid institutes grant nothing
            until they're operational.
          </p>
          <ul className="space-y-1">
            {institutes.map((inst) => {
              const operational = inst.status === "active" && inst.billing_ok;
              return (
                <li key={inst.id}>
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedInstitutes.has(inst.id)}
                      disabled={busy}
                      onChange={() => toggleInstitute(inst.id)}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: "var(--gold)" }}
                      aria-label={`Institute ${inst.name} in ${group.name}`}
                    />
                    <span className="truncate">{inst.name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0 tabular-nums">
                      {inst.seats_used}/{inst.seats_purchased} seats
                    </span>
                    {!operational && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-rose-100 text-rose-700 shrink-0" title="Not operational: its members don't get access until it's approved and paid">
                        {inst.status === "pending" ? "Pending" : inst.status === "disabled" ? "Disabled" : "Payment lapsed"}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Members */}
      <div>
        <div className="text-xs font-medium text-slate-600 mb-1.5">Members</div>
        {(group.members || []).length === 0 && (
          <p className="text-xs text-slate-400 mb-1.5">
            {selectedInstitutes.size > 0 ? "No individual members (institute members are included above)." : "No members yet."}
          </p>
        )}
        <ul className="space-y-1 mb-2">
          {(group.members || []).map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-slate-700">
                {m.display_name !== m.email && <span className="font-medium">{m.display_name} · </span>}
                <span className={m.display_name !== m.email ? "text-slate-500" : ""}>{m.email}</span>
              </span>
              <button
                type="button"
                onClick={() => removeMember(m)}
                disabled={busy}
                className="text-xs text-rose-500 hover:bg-rose-50 px-2 py-0.5 rounded disabled:opacity-50 shrink-0"
                aria-label={`Remove ${m.email} from ${group.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <form
          onSubmit={(e) => { e.preventDefault(); addMember(); }}
          className="relative"
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowSuggestions(false); }}
        >
          <div className="flex gap-2">
            <input
              type="email"
              value={memberEmail}
              onChange={(e) => { setMemberEmail(e.target.value); setMemberMsg(null); setShowSuggestions(true); searchUsers(e.target.value); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Add member by email"
              className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              aria-label={`Add member to ${group.name} by email`}
              autoComplete="off"
            />
            <button type="submit" disabled={busy || !memberEmail.trim()} className="px-3 py-1.5 rounded-lg bg-[var(--gold)] text-[var(--ink)] text-xs font-semibold disabled:opacity-50 shrink-0">
              Add
            </button>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute left-0 right-0 mt-1 z-10 rounded-lg border border-slate-200 bg-white shadow-lg max-h-40 overflow-y-auto" role="listbox">
              {suggestions.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addMember(u.email)}
                    className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {u.email}
                    {u.username && u.username !== u.email && <span className="text-slate-400"> · {u.username}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </form>
        {memberMsg && <p className="text-xs text-emerald-700 mt-1">{memberMsg}</p>}
      </div>
    </div>
  );
}
